import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getItemFromLocalStorageAsObject,
  saveItemToLocalStorage,
} from "../utils/localStorage.ts";

const internalStorageEvent = "internal-storage";

declare global {
  interface WindowEventMap {
    [internalStorageEvent]: CustomEvent;
  }
}

export enum LocalStorageKey {
  FractionPersist = "persist",
}

interface InternalStorageEventData<T> {
  newValue: T;
  key: LocalStorageKey;
}

export function useLocalStorageValue<T = unknown>(
  key: LocalStorageKey
): [T | undefined, (newValue: T) => void] {
  const [value, setValue] = useState<T | undefined>(
    getItemFromLocalStorageAsObject(key)
  );

  const onStorageEvent = useCallback(
    (e: CustomEvent<InternalStorageEventData<T>>) => {
      if (e.detail.key === key) {
        setValue(e.detail.newValue);
      }
    },
    [key]
  );

  useEffect(() => {
    window.addEventListener(internalStorageEvent, onStorageEvent);
    return () => {
      window.removeEventListener(internalStorageEvent, onStorageEvent);
    };
  }, [onStorageEvent]);

  const updateValue = useCallback(
    (newValue: T) => {
      saveItemToLocalStorage(key, newValue);
      // native "storage" event not supported for same-document changes
      dispatchEvent(
        new CustomEvent<InternalStorageEventData<T>>(internalStorageEvent, {
          detail: { newValue, key },
        })
      );
    },
    [key]
  );

  return useMemo(() => [value, updateValue], [value, updateValue]);
}
